'use client';

import React, { useRef, useState, useEffect } from 'react';

/**
 * @typedef {Object} ProgressBarProps
 * @property {number} value - Current progress value (0-100)
 * @property {number} [min=0] - Minimum value
 * @property {number} [max=100] - Maximum value
 * @property {'horizontal' | 'vertical'} [orientation='horizontal'] - Orientation of the progress bar
 * @property {boolean} [showThumb=false] - Whether to show a draggable thumb
 * @property {boolean} [disabled=false] - Whether the progress bar is disabled
 * @property {(value: number) => void} [onChange] - Callback when value changes (if thumb is draggable)
 * @property {(value: number) => void} [onChangeComplete] - Callback when drag ends
 * @property {React.CSSProperties} [style] - Custom styles for the root container
 * @property {React.CSSProperties} [trackStyle] - Custom styles for the track
 * @property {React.CSSProperties} [progressStyle] - Custom styles for the progress fill
 * @property {React.CSSProperties} [thumbStyle] - Custom styles for the thumb
 * @property {string} [className] - Custom className for the root container
 * @property {string} [trackClassName] - Custom className for the track
 * @property {string} [progressClassName] - Custom className for the progress fill
 * @property {string} [thumbClassName] - Custom className for the thumb
 * @property {string} [ariaLabel] - Accessibility label
 * @property {string} [ariaValueText] - Accessibility value text
 */

const ProgressBar = ({
  value,
  min = 0,
  max = 100,
  orientation = 'horizontal',
  showThumb = false,
  disabled = false,
  onChange,
  onChangeComplete,
  style = {},
  trackStyle = {},
  progressStyle = {},
  thumbStyle = {},
  className = '',
  trackClassName = '',
  progressClassName = '',
  thumbClassName = '',
  ariaLabel,
  ariaValueText,
}) => {
  const trackRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);

  const isHorizontal = orientation === 'horizontal';

  // Normalize value to percentage
  const percentage = Math.min(100, Math.max(0, ((currentValue - min) / (max - min)) * 100));

  // Update internal value when prop changes
  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  /**
   * Calculate value from mouse/touch position
   * @param {number} clientX - X coordinate
   * @param {number} clientY - Y coordinate
   * @returns {number} Calculated value
   */
  const calculateValueFromPosition = (clientX, clientY) => {
    if (!trackRef.current) return currentValue;

    const rect = trackRef.current.getBoundingClientRect();
    let ratio;

    if (isHorizontal) {
      const x = clientX - rect.left;
      ratio = Math.max(0, Math.min(1, x / rect.width));
    } else {
      const y = clientY - rect.top;
      ratio = 1 - Math.max(0, Math.min(1, y / rect.height));
    }

    return min + ratio * (max - min);
  };

  /**
   * Handle mouse down event
   * @param {React.MouseEvent} e - Mouse event
   */
  const handleMouseDown = (e) => {
    if (disabled || !showThumb) return;

    setIsDragging(true);
    const newValue = calculateValueFromPosition(e.clientX, e.clientY);
    setCurrentValue(newValue);
    onChange?.(newValue);
  };

  /**
   * Handle mouse move event
   * @param {MouseEvent} e - Mouse event
   */
  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const newValue = calculateValueFromPosition(e.clientX, e.clientY);
    setCurrentValue(newValue);
    onChange?.(newValue);
  };

  /**
   * Handle mouse up event
   */
  const handleMouseUp = () => {
    if (!isDragging) return;

    setIsDragging(false);
    onChangeComplete?.(currentValue);
  };

  /**
   * Handle touch start event
   * @param {React.TouchEvent} e - Touch event
   */
  const handleTouchStart = (e) => {
    if (disabled || !showThumb) return;

    setIsDragging(true);
    const touch = e.touches[0];
    const newValue = calculateValueFromPosition(touch.clientX, touch.clientY);
    setCurrentValue(newValue);
    onChange?.(newValue);
  };

  /**
   * Handle touch move event
   * @param {TouchEvent} e - Touch event
   */
  const handleTouchMove = (e) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    const newValue = calculateValueFromPosition(touch.clientX, touch.clientY);
    setCurrentValue(newValue);
    onChange?.(newValue);
  };

  /**
   * Handle keyboard navigation
   * @param {React.KeyboardEvent} e - Keyboard event
   */
  const handleKeyDown = (e) => {
    if (disabled || !showThumb) return;

    let newValue = currentValue;
    const step = (max - min) / 100;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        e.preventDefault();
        newValue = Math.min(max, currentValue + step);
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        e.preventDefault();
        newValue = Math.max(min, currentValue - step);
        break;
      case 'Home':
        e.preventDefault();
        newValue = min;
        break;
      case 'End':
        e.preventDefault();
        newValue = max;
        break;
      default:
        return;
    }

    setCurrentValue(newValue);
    onChange?.(newValue);
    onChangeComplete?.(newValue);
  };

  // Add/remove global event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging, currentValue]);

  // Default styles
  const defaultContainerStyle = {
    position: 'relative',
    display: 'inline-block',
    width: isHorizontal ? '100%' : 'auto',
    height: isHorizontal ? 'auto' : '100%',
    ...style,
  };

  const defaultTrackStyle = {
    position: 'relative',
    width: isHorizontal ? '100%' : '12px',
    height: isHorizontal ? '12px' : '100%',
    backgroundColor: '#3A3B3B',
    overflow: 'visible',
    cursor: showThumb && !disabled ? 'pointer' : 'default',
    ...trackStyle,
  };

  const defaultProgressStyle = {
    position: 'absolute',
    left: 0,
    width: isHorizontal ? `${percentage}%` : '100%',
    height: isHorizontal ? '100%' : `${percentage}%`,
    backgroundColor: '#FECB04',
    transition: isDragging ? 'none' : 'width 0.2s ease, height 0.2s ease',
    ...(isHorizontal ? { top: 0 } : { bottom: 0 }),
    ...progressStyle,
  };

  const defaultThumbStyle = {
    position: 'absolute',
    width: '16px',
    height: '16px',
    backgroundColor: '#FECB04',
    borderRadius: '50%',
    cursor: disabled ? 'not-allowed' : 'grab',
    top: isHorizontal ? '50%' : 'auto',
    bottom: isHorizontal ? 'auto' : `${percentage}%`,
    left: isHorizontal ? `${percentage}%` : '50%',
    transform: isHorizontal ? 'translate(-50%, -50%)' : 'translate(-50%, 50%)',
    transition: isDragging ? 'none' : 'top 0.2s ease, left 0.2s ease, bottom 0.2s ease',
    zIndex: 10,
    ...thumbStyle,
  };

  return (
    <div
      className={className}
      style={defaultContainerStyle}
      role={showThumb ? 'slider' : 'progressbar'}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={currentValue}
      aria-valuetext={ariaValueText}
      aria-label={ariaLabel || `Progress: ${Math.round(percentage)}%`}
      aria-orientation={orientation}
      aria-disabled={disabled}
      tabIndex={showThumb && !disabled ? 0 : -1}
      onKeyDown={handleKeyDown}
    >
      {/* Hidden native input for form compatibility */}
      {showThumb && (
        <input
          type="range"
          min={min}
          max={max}
          value={currentValue}
          onChange={() => {}} // Controlled by our custom handlers
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            opacity: 0,
            pointerEvents: 'none',
          }}
          tabIndex={-1}
          aria-hidden="true"
        />
      )}

      <div
        ref={trackRef}
        className={trackClassName}
        style={defaultTrackStyle}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div className={progressClassName} style={defaultProgressStyle} />

        {showThumb && (
          <div
            className={thumbClassName}
            style={{
              ...defaultThumbStyle,
              cursor: isDragging ? 'grabbing' : disabled ? 'not-allowed' : 'grab',
              opacity: disabled ? 0.5 : 1,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ProgressBar;
